import { Grid, makeStyles } from '@material-ui/core'
import ProfilePicture from 'components/common/ProfilePicture'
import React, { useMemo } from 'react'
import HoveredActions from 'components/common/HoveredActions'
import OpenInNewTabIcon from 'components/common/icons/OpenInNewTabIcon'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import PatientStatus from 'components/Patients/PatientStatus'
import { ROUTES } from 'consts'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { getEnvironmentHostname } from 'utils/awsUtils'
import useCopilotPatientsList from 'components/CopilotControl/hooks/useCopilotPatientsList'
import { trackEvent } from 'utils/analyticsUtils'
import NextScanDate from './NextScanDate'

const SCANNING_LAYOUT_STYLES = {
  patientInfo: { width: '28%' },
  statusIcon: { width: '8%', textAlign: 'center' },
  scanDate: { width: '28%' },
  lastScan: { width: '18%' },
  lastComm: { width: '18%' }
}

const useStyles = makeStyles(() => ({
  patientItem: {
    position: 'relative',
    display: 'flex',
    padding: 20,
    alignItems: 'center',
    borderBottom: '1px solid #E9E9E9'
  },
  avatar: {
    maxWidth: 80
  },
  patientNameStatusContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 10,
    minWidth: 0,
    flex: 1
  },
  lastCommunicationLabel: {
    color: '#727B8C',
    paddingBottom: 5
  },
  truncatedText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  centeredIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%'
  },
  statusIconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
}))

const PatientsScanningScheduleListItem = ({ patient, scoreOptions, index }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { getPatientStatusObject } = useCopilotPatientsList()

  const {
    id: patientId,
    name: patientName,
    profilePicture,
    statusKey,
    program,
    lastGrinScanUploadDate,
    copilotScanningScheduleScoreCategory,
    currentCycleOriginalEndDate: nextScanDate,
    lastMessageTimestamp
  } = patient

  const actions = useMemo(
    () =>
      [
        {
          key: 'openPatient',
          icon: <OpenInNewTabIcon />,
          tooltip: t('pages.copilotControl.widgets.needsAttention.actions.openPatient'),
          onClick: () => {
            trackEvent('Copilot Control - Patient opened from list', {
              view: 'Scanning Schedule',
              patientId
            })
            window.open(`${getEnvironmentHostname()}${ROUTES.PATIENTS}/${patientId}`, '_blank')
          },
          condition: () => true
        }
      ].filter(action => action.condition()),
    [patientId, t]
  )
  const calculatedLastScanDate = lastGrinScanUploadDate ? moment(lastGrinScanUploadDate).format('MMM D') : '-'

  const patientStatus = getPatientStatusObject(statusKey, program)
  const scoreOption = scoreOptions[copilotScanningScheduleScoreCategory]
  const PatientCategoryIcon = scoreOptions[copilotScanningScheduleScoreCategory].icon

  const [isHovered, setIsHovered] = React.useState(false)

  return (
    <Grid
      key={index}
      container
      className={classes.patientItem}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Grid item style={SCANNING_LAYOUT_STYLES.patientInfo}>
        <Grid container>
          <Grid item className={classes.avatar}>
            <ProfilePicture name={patientName} photo={profilePicture} />
          </Grid>
          <Grid item className={classes.patientNameStatusContainer}>
            <Grid item>
              <DazzedParagraph14 className={classes.truncatedText}>{patientName}</DazzedParagraph14>
            </Grid>
            <Grid item>
              <PatientStatus status={patientStatus} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item style={SCANNING_LAYOUT_STYLES.statusIcon} className={classes.statusIconContainer}>
        <div className={classes.centeredIcon}>
          <PatientCategoryIcon
            size="small"
            icon={scoreOption.icon}
            fillColor={scoreOption.fillColor}
            strokeColor={scoreOption.strokeColor}
            value={scoreOption.value || 0}
          />
        </div>
      </Grid>
      <Grid item style={SCANNING_LAYOUT_STYLES.scanDate}>
        <NextScanDate nextScanDate={nextScanDate} />
      </Grid>
      <Grid item style={SCANNING_LAYOUT_STYLES.lastScan}>
        <Grid container direction="column">
          <Grid item className={classes.lastCommunicationLabel}>
            {t('pages.copilotControl.widgets.scanningSchedule.lastScanLabel')}
          </Grid>
          <Grid item>{calculatedLastScanDate}</Grid>
        </Grid>
      </Grid>
      <Grid item style={SCANNING_LAYOUT_STYLES.lastComm}>
        <div className={classes.lastCommunicationLabel}>Last Comm:</div>
        <div>{moment(lastMessageTimestamp).format('MMM D, HH:mm')}</div>
      </Grid>
      <HoveredActions actions={actions} isOpen={isHovered} placement="top" />
    </Grid>
  )
}

export default PatientsScanningScheduleListItem
